import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import '../trackorders.css';
import NoData from "../NoData";
import Loading from "../Loading";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../state/store";
import { fetchOrdersAndProducts, fetchOrdersAndProductsUsingid } from "../../state/slices/orderSlice";
import { useAuth } from "../../context/AuthContext";
import MainNavbar from "../../pages/MainNavbar";
import Breadcrumbs from "./../Breadcrumbs";
import Footer from "./../footers";
import { Order, Product, OrderItem } from "../../interfaces/orders_manage";
import { availableIcons } from "../../utils/icons";
import { OrderCard } from "./OrderCard";

// Create icon map
const iconMap = new Map<string, React.ComponentType<any>>(
  availableIcons.map(icon => [icon.name, icon.component as React.ComponentType<any>])
);

// Icon rendering function
const renderIcon = (iconName?: string | null) => {
  if (!iconName) return <span className="subnavbar__icon">📂</span>;
  const Icon = iconMap.get(iconName);
  return Icon ? <Icon className="subnavbar__icon" /> : <span className="subnavbar__icon">📂</span>;
};

// MAIN TRACKORDERS COMPONENT
const TrackOrders: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [showSubnav, setShowSubnav] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [showFilters, setShowFilters] = useState(false);

  const { orders, products, loading, error } = useSelector((state: RootState) => state.orders);
  const userId = currentUser?.id || "";
  const isLoggedIn = !!currentUser;



 useEffect(() => {
  if (currentUser?.id) {
    dispatch(fetchOrdersAndProductsUsingid(currentUser.id));
  }
}, [dispatch, currentUser?.id]);


  // Create enriched orders with product details
// Create enriched orders with product details
const enrichedOrders = useMemo(() => {
  if (!orders || !products) return [];

  const productMap = new Map<string, Product>(
    products.map((product: Product) => [String(product.id), product])
  );

  return orders
    .filter(order => order.userId === currentUser?.id)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()) // Sort by newest first
    .map((order: Order) => ({
      ...order,
      items: order.items.map((item: OrderItem) => ({
        ...item,
        product: productMap.get(String(item.productId))
      }))
    }));
}, [orders, products, currentUser?.id]);



  // Filter orders by status
  const filteredOrders = useMemo(() => {
    if (statusFilter === 'All') return enrichedOrders;
    return enrichedOrders.filter(order => 
      order.status.toLowerCase() === statusFilter.toLowerCase()
    );
  }, [enrichedOrders, statusFilter]);

  // Handle error state
  if (error) {
    return (
      <div className="error-container">
        <h2>Error Loading Orders</h2>
        <p>{error}</p>
        <button onClick={() => dispatch(fetchOrdersAndProducts())}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <MainNavbar
        searchQuery=""
        setSearchQuery={() => { }}
        isLoggedIn={isLoggedIn}
        showSubnav={showSubnav}
        setShowSubnav={setShowSubnav}
        userId={userId}
        disableSearch={true}
      />

      <Breadcrumbs />

      <div className="tracking_order_details">
        <div className="tracking_header">
          <h2><span className="order-icon">{renderIcon('FiPackage')}</span> Order History</h2>
          <p className="subtitle">View and manage your recent purchases</p>
          
          <div className="filter-section">
            <button 
              className="filter-toggle"
              onClick={() => setShowFilters(!showFilters)}
            >
              {renderIcon('FiFilter')} {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
            
            {showFilters && (
              <div className="filters-container">
                <div className="filters-header">
                  <h3>Filter Orders</h3>
                  <button 
                    className="close-filters"
                    onClick={() => setShowFilters(false)}
                  >
                    {renderIcon('FiX')}
                  </button>
                </div>
                <div className="status-filters">
                  {['All', 'Processing', 'Shipped', 'Delivered', 'Cancelled', 'Pending'].map(status => (
                    <button
                      key={status}
                      className={`filter-btn ${statusFilter === status ? 'active' : ''}`}
                      onClick={() => {
                        setStatusFilter(status);
                        setShowFilters(false);
                      }}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {statusFilter !== 'All' && (
              <div className="active-filter">
                Showing: {statusFilter}
                <button 
                  className="clear-filter"
                  onClick={() => setStatusFilter('All')}
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        </div>
        
        {loading ? (
          <Loading message="Loading your orders..." />
        ) : filteredOrders.length === 0 ? (
          <NoData 
            message="No orders found" 
            subtitle={statusFilter === 'All' 
              ? "Your order history will appear here once you make a purchase" 
              : `No orders found with status: ${statusFilter}`}
            actionText="Continue Shopping"
            onAction={() => navigate('/')}
          />
        ) : (
          <div className="orders-container">
            <div className="orders-grid">
              {filteredOrders.map((order) => (
                <OrderCard 
                  key={order.id} 
                  order={order} 
                  navigate={navigate} 
                />
              ))}
            </div>
          </div>
        )}
      </div>
      <Footer/>
    </>
  );
};

export default TrackOrders;